/**
 * Tour · wave-help-tour-mvp · 2026-05-20
 *
 * Overlay interativo tipo Intro.js — 8 steps cobrindo as features MVP.
 * Independente do sistema `apps/web/src/onboarding/*` (server-side tour)
 * que ainda existe pra waves antigas. Esta é a v2: client-only, leve,
 * triggered por:
 *
 *   1. WelcomePage CTA "Conhecer Builder em 2 minutos"
 *   2. Topbar settings menu → "Refazer tour"
 *   3. Auto first-time após criar primeira obra (não no login direto)
 *
 * Estado vive em localStorage chave `builder.tour-completed-v2`. Quando
 * está marcado como concluído ou pulado, o tour não auto-abre mais.
 *
 * Visual contract (DESIGN.md product register):
 *   - Mask escura overlay (rgba(15,17,22,0.55)) com window-cutout para o
 *     target. Cutout é um SVG mask + rect, sem layout shift.
 *   - Card de explicação flutua ao lado/abaixo do target, mobile-first
 *     stack (default bottom-center).
 *   - Botões: primary "Próximo" + ghost "Voltar" + skip persistente.
 *   - Easing: ease-out-soft 220ms entre steps (fade + 4px translate).
 *   - Touch: botões >=44px (size="md").
 *   - Linguagem PT-BR / EN via i18n `tour.*`.
 *
 * Princípios honored (PRODUCT.md):
 *   - Sem modal pesado · overlay leve, dismissable a qualquer hora
 *   - Sempre responde "o que faço agora": botão primary destacado
 *   - Mobile-first: 375x667 funciona sem scroll horizontal
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

// ───────────────────────── storage helpers ────────────────────────────

/**
 * Chave única, V2 pra coexistir sem conflito com `apps/web/src/onboarding/*`
 * que usa server-side state. Bump pra V3 quando alterar # de steps ou
 * sequência semântica.
 */
export const TOUR_STORAGE_KEY = 'builder.tour-completed-v2';

export function isTourCompleted(): boolean {
  try {
    return globalThis.localStorage?.getItem(TOUR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function markTourCompleted(): void {
  try {
    globalThis.localStorage?.setItem(TOUR_STORAGE_KEY, '1');
  } catch {
    // localStorage indisponível (private mode, etc) — silencioso.
  }
}

export function resetTourCompleted(): void {
  try {
    globalThis.localStorage?.removeItem(TOUR_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ───────────────────────── step definitions ───────────────────────────

export type TourStepId =
  | 'welcome'
  | 'search'
  | 'chat'
  | 'inbox'
  | 'hoje'
  | 'trena'
  | 'comando'
  | 'done';

interface TourStepDef {
  id: TourStepId;
  /**
   * CSS selector pro elemento a destacar. Resolvido em runtime no DOM.
   * Quando null, o step é centralizado (welcome / done).
   */
  selector: string | null;
  /**
   * Se true, o step é admin-only. Skipped pros outros roles. Mantemos
   * a sequência total em 8 pra facilitar copy "Passo X de 8".
   */
  adminOnly?: boolean;
}

/**
 * 8 steps canônicos. Selectors casam com data-testid de elementos
 * existentes da TopBar/SideNav. Se algum elemento não existir no DOM
 * atual (ex: usuário não é admin), o step ainda renderiza num placement
 * central — não quebra.
 */
export const TOUR_STEPS: readonly TourStepDef[] = [
  { id: 'welcome', selector: null },
  { id: 'search', selector: '[aria-label*="Buscar"], [aria-label*="Search"]' },
  { id: 'chat', selector: '[data-testid="chat-instant-launcher"]' },
  { id: 'inbox', selector: '[data-testid="inbox-bell"]' },
  { id: 'hoje', selector: '[data-testid="builder-logo"]' },
  { id: 'trena', selector: '[data-testid="builder-logo"]' },
  { id: 'comando', selector: '[data-testid="builder-logo"]', adminOnly: true },
  { id: 'done', selector: null },
];

// ─────────────────────────── geometry ─────────────────────────────────

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const FALLBACK_RECT: Rect = { top: 0, left: 0, width: 0, height: 0 };

/** Padding visual do cutout em torno do target. */
const CUTOUT_PADDING = 8;

function readRect(el: Element | null): Rect {
  if (!el) return FALLBACK_RECT;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - CUTOUT_PADDING,
    left: r.left - CUTOUT_PADDING,
    width: r.width + CUTOUT_PADDING * 2,
    height: r.height + CUTOUT_PADDING * 2,
  };
}

// ─────────────────────────── component ────────────────────────────────

export interface TourProps {
  open: boolean;
  /** Callback quando usuário pula ou conclui. Marca localStorage. */
  onClose: () => void;
  /** Whether user is admin. Hides admin-only step `comando`. */
  isAdmin?: boolean;
  /** For tests: skip portal so render is observable in JSDOM. */
  inline?: boolean;
}

export function Tour({ open, onClose, isAdmin = false, inline = false }: TourProps): JSX.Element | null {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const [stepIdx, setStepIdx] = useState(0);
  const [targetRect, setTargetRect] = useState<Rect>(FALLBACK_RECT);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Reset cursor toda vez que abre.
  useEffect(() => {
    if (open) setStepIdx(0);
  }, [open]);

  const steps = useMemo(
    () => TOUR_STEPS.filter((s) => !s.adminOnly || isAdmin),
    [isAdmin],
  );

  const step = steps[stepIdx] ?? steps[steps.length - 1]!;
  const totalSteps = steps.length;

  // Recalcula posição do target quando o step muda ou janela redimensiona.
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      if (!step.selector) {
        setTargetRect(FALLBACK_RECT);
        return;
      }
      const el = document.querySelector(step.selector);
      setTargetRect(readRect(el));
    };
    compute();
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, step]);

  const stepCopy = t.tour.steps[step.id];

  const finish = useCallback(() => {
    markTourCompleted();
    onClose();
  }, [onClose]);

  const skip = useCallback(() => {
    markTourCompleted();
    onClose();
  }, [onClose]);

  const goNext = useCallback(() => {
    if (stepIdx >= totalSteps - 1) {
      finish();
      return;
    }
    setStepIdx((i) => i + 1);
  }, [stepIdx, totalSteps, finish]);

  const goBack = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1));
  }, []);

  // ESC para pular.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goBack();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, skip, goNext, goBack]);

  const hasTarget = step.selector !== null && targetRect.width > 0;
  const isLast = stepIdx === totalSteps - 1;
  const isFirst = stepIdx === 0;

  /**
   * Placement do card: por padrão bottom-center. Se houver target visível
   * na metade superior da viewport, mostra abaixo dele. Se na metade
   * inferior, mostra acima. Mobile sempre stack na base.
   *
   * IMPORTANTE: hook MORA antes do early-return abaixo. Mover pra dentro
   * do return condicional causa "Rendered more hooks than during the
   * previous render" porque o set de hooks ativos muda entre renderizações.
   */
  const cardStyle = useMemo<React.CSSProperties>(() => {
    if (!hasTarget) {
      return {
        bottom: '32px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    }
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const isTopHalf = targetRect.top + targetRect.height / 2 < viewportH / 2;
    if (isTopHalf) {
      return {
        top: `${Math.min(targetRect.top + targetRect.height + 16, viewportH - 200)}px`,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    }
    return {
      bottom: `${Math.max(viewportH - targetRect.top + 16, 24)}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    };
  }, [hasTarget, targetRect]);

  if (!open) return null;

  const content = (
    <div
      ref={containerRef}
      data-testid="tour-root"
      role="dialog"
      aria-modal="true"
      aria-label={isEn ? 'Interactive Builder tour' : 'Tour interativo do Builder'}
      className="fixed inset-0 z-[80] pointer-events-none"
    >
      {/* Mascara SVG: cobre tudo com fill escuro e abre cutout no target. */}
      <svg
        data-testid="tour-mask"
        className="pointer-events-auto absolute inset-0 h-full w-full"
        aria-hidden
        onClick={skip}
      >
        <defs>
          <mask id="tour-cutout-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hasTarget && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(12, 14, 20, 0.62)"
          mask="url(#tour-cutout-mask)"
        />
        {hasTarget && (
          <rect
            x={targetRect.left}
            y={targetRect.top}
            width={targetRect.width}
            height={targetRect.height}
            rx="12"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* Skip permanente no topo direito (sempre visível). */}
      <div className="pointer-events-auto absolute right-3 top-3 md:right-6 md:top-6">
        <button
          type="button"
          data-testid="tour-skip"
          onClick={skip}
          className={cn(
            'rounded-full bg-surface-2/95 px-3 py-2 text-[13px] font-medium text-ink',
            'border border-line-strong shadow-elev-1',
            'hover:bg-surface-3 transition-colors duration-150',
            'min-h-[44px]',
          )}
        >
          {t.tour.skip}
        </button>
      </div>

      {/* Card flutuante com explicação + nav. */}
      <div
        data-testid="tour-card"
        className={cn(
          'pointer-events-auto absolute',
          'w-[min(420px,calc(100vw-32px))]',
          'rounded-2xl border border-line-strong bg-surface-2',
          'p-5 shadow-elev-2',
          'animate-fade-in-up',
        )}
        style={{
          ...cardStyle,
          animationDuration: '220ms',
          animationTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      >
        <p
          data-testid="tour-counter"
          className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-subtle"
        >
          {t.tour.counter(stepIdx + 1, totalSteps)}
        </p>
        <h3
          data-testid="tour-title"
          className="mt-1 text-lg font-semibold tracking-tight text-ink"
        >
          {stepCopy.title}
        </h3>
        <p
          data-testid="tour-body"
          className="mt-2 text-[14px] leading-relaxed text-ink-muted"
        >
          {stepCopy.body}
        </p>

        <div className="mt-5 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="md"
            onClick={goBack}
            disabled={isFirst}
            data-testid="tour-back"
          >
            {t.tour.back}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={goNext}
            data-testid="tour-next"
          >
            {isLast ? t.tour.finish : t.tour.next}
          </Button>
        </div>
      </div>
    </div>
  );

  // Em produção montamos via portal pra escapar de overflow de pais.
  // Em testes (`inline`) renderizamos direto pra facilitar query no JSDOM.
  if (inline || typeof document === 'undefined') return content;
  return createPortal(content, document.body);
}

export default Tour;
